import React, { useRef, useEffect, useState, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Sphere, MeshDistortMaterial, useGLTF } from '@react-three/drei';
import * as THREE from 'three';

// Viseme mapping based on audio frequency analysis
const VISEMES = {
  SILENT: 'silent',      // No sound
  AA: 'aa',             // "father" - open mouth
  E: 'e',               // "bed" - slightly open
  I: 'i',               // "see" - wide smile
  O: 'o',               // "go" - rounded
  U: 'u',               // "too" - puckered
  M: 'm',               // "mother" - closed
  L: 'l',               // "love" - tongue
  F: 'f',               // "face" - teeth on lip
  S: 's'                // "see" - teeth together
};

// Frequency ranges for viseme detection (in Hz)
const FREQUENCY_VISEME_MAP = [
  { range: [0, 200], viseme: VISEMES.M },      // Low freq - closed mouth sounds
  { range: [200, 400], viseme: VISEMES.AA },   // Mid-low - open vowels
  { range: [400, 800], viseme: VISEMES.E },    // Mid - medium vowels
  { range: [800, 1500], viseme: VISEMES.I },   // Mid-high - high vowels
  { range: [1500, 3000], viseme: VISEMES.S },  // High - sibilants
  { range: [3000, 8000], viseme: VISEMES.F }   // Very high - fricatives
];

// Realistic Human Avatar Head
function AvatarHead({ isSpeaking, audioLevel, visemeWeights }) {
  const groupRef = useRef();
  const headRef = useRef();
  const leftEyeRef = useRef();
  const rightEyeRef = useRef();
  const mouthRef = useRef();
  
  const [hue, setHue] = useState(25); // Natural skin tone
  const [blinkTimer, setBlinkTimer] = useState(0);
  const [isBlinking, setIsBlinking] = useState(false);

  useFrame((state) => {
    if (groupRef.current) {
      // Natural idle animation - gentle breathing motion
      if (!isSpeaking) {
        groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.8) * 0.06;
        groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.04;
      } else {
        // Speaking animation - subtle head movement
        groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 2) * 0.04;
        groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 1.5) * 0.02;
        groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 1.8) * 0.03;
      }
    }

    // Natural blinking animation
    const currentTime = state.clock.elapsedTime;
    if (currentTime - blinkTimer > (isBlinking ? 0.15 : 3 + Math.random() * 2)) {
      setIsBlinking(!isBlinking);
      setBlinkTimer(currentTime);
    }

    // Apply blink to eyes
    if (leftEyeRef.current && rightEyeRef.current) {
      const blinkScale = isBlinking ? 0.1 : 1;
      leftEyeRef.current.scale.y = blinkScale;
      rightEyeRef.current.scale.y = blinkScale;
    }

    // Subtle color variation for natural skin
    setHue(25 + Math.sin(state.clock.elapsedTime * 0.1) * 3);
  });

  // Calculate mouth shape based on viseme
  const getMouthShape = () => {
    if (!isSpeaking || !visemeWeights) {
      return { scaleX: 0.3, scaleY: 0.1, scaleZ: 0.3, posY: -0.5 };
    }

    // Blend viseme shapes
    const { aa, e, i, o, u, m, s, f } = visemeWeights;
    
    // Base mouth dimensions
    let scaleX = 0.3;
    let scaleY = 0.1;
    let scaleZ = 0.3;
    let posY = -0.5;

    // AA - open mouth
    scaleX += aa * 0.3;
    scaleY += aa * 0.4;
    posY -= aa * 0.1;

    // E - medium open
    scaleX += e * 0.25;
    scaleY += e * 0.2;

    // I - wide smile
    scaleX += i * 0.4;
    scaleY += i * 0.05;

    // O - rounded
    scaleZ += o * 0.2;
    scaleY += o * 0.3;

    // U - puckered
    scaleX -= u * 0.15;
    scaleZ += u * 0.15;
    scaleY += u * 0.2;

    // M - closed
    scaleY *= (1 - m * 0.7);

    // S - teeth together
    scaleY += s * 0.1;

    // F - lip bite
    posY += f * 0.05;
    scaleY += f * 0.05;

    return { scaleX, scaleY, scaleZ, posY };
  };

  const mouthShape = getMouthShape();

  return (
    <group ref={groupRef}>
      {/* Main head - realistic skin */}
      <Sphere ref={headRef} args={[1.5, 64, 64]} position={[0, 0, 0]}>
        <meshStandardMaterial
          color={`hsl(${hue}, 40%, 70%)`}
          roughness={0.6}
          metalness={0.05}
        />
      </Sphere>

      {/* Neck */}
      <mesh position={[0, -1.8, 0]}>
        <cylinderGeometry args={[0.6, 0.8, 0.8, 32]} />
        <meshStandardMaterial 
          color={`hsl(${hue}, 38%, 68%)`} 
          roughness={0.65}
        />
      </mesh>

      {/* Hair - dark brown/black */}
      <Sphere args={[1.58, 32, 32]} position={[0, 0.5, -0.3]} scale={[1, 1.15, 0.85]}>
        <meshStandardMaterial 
          color="#2a1810" 
          roughness={0.85}
        />
      </Sphere>

      {/* Forehead hairline detail */}
      <Sphere args={[1.52, 32, 16]} position={[0, 0.8, 0.5]} scale={[1, 0.3, 1]}>
        <meshStandardMaterial 
          color="#2a1810" 
          roughness={0.85}
        />
      </Sphere>

      {/* Eyes - white sclera */}
      <Sphere ref={leftEyeRef} args={[0.2, 32, 32]} position={[-0.48, 0.38, 1.15]}>
        <meshStandardMaterial 
          color="#ffffff" 
          roughness={0.3}
          metalness={0.1}
        />
      </Sphere>
      <Sphere ref={rightEyeRef} args={[0.2, 32, 32]} position={[0.48, 0.38, 1.15]}>
        <meshStandardMaterial 
          color="#ffffff" 
          roughness={0.3}
          metalness={0.1}
        />
      </Sphere>

      {/* Iris - brown/blue */}
      <Sphere args={[0.11, 32, 32]} position={[-0.48, 0.38, 1.33]}>
        <meshStandardMaterial 
          color="#4a6fa5" 
          roughness={0.2}
        />
      </Sphere>
      <Sphere args={[0.11, 32, 32]} position={[0.48, 0.38, 1.33]}>
        <meshStandardMaterial 
          color="#4a6fa5" 
          roughness={0.2}
        />
      </Sphere>

      {/* Pupils - black */}
      <Sphere args={[0.06, 32, 32]} position={[-0.48, 0.38, 1.38]}>
        <meshStandardMaterial color="#000000" />
      </Sphere>
      <Sphere args={[0.06, 32, 32]} position={[0.48, 0.38, 1.38]}>
        <meshStandardMaterial color="#000000" />
      </Sphere>

      {/* Eye highlights - natural reflection */}
      <Sphere args={[0.04, 16, 16]} position={[-0.45, 0.42, 1.40]}>
        <meshStandardMaterial 
          color="#ffffff" 
          emissive="#ffffff" 
          emissiveIntensity={0.8}
        />
      </Sphere>
      <Sphere args={[0.04, 16, 16]} position={[0.51, 0.42, 1.40]}>
        <meshStandardMaterial 
          color="#ffffff" 
          emissive="#ffffff" 
          emissiveIntensity={0.8}
        />
      </Sphere>

      {/* Eyelids */}
      <mesh position={[-0.48, 0.48, 1.25]} rotation={[0.3, 0, 0]}>
        <boxGeometry args={[0.45, 0.08, 0.15]} />
        <meshStandardMaterial 
          color={`hsl(${hue}, 35%, 65%)`} 
          roughness={0.5}
        />
      </mesh>
      <mesh position={[0.48, 0.48, 1.25]} rotation={[0.3, 0, 0]}>
        <boxGeometry args={[0.45, 0.08, 0.15]} />
        <meshStandardMaterial 
          color={`hsl(${hue}, 35%, 65%)`} 
          roughness={0.5}
        />
      </mesh>

      {/* Eyebrows - natural */}
      <mesh position={[-0.48, 0.68, 1.22]} rotation={[0, 0, -0.15]}>
        <boxGeometry args={[0.4, 0.1, 0.1]} />
        <meshStandardMaterial 
          color="#2a1810" 
          roughness={0.9}
        />
      </mesh>
      <mesh position={[0.48, 0.68, 1.22]} rotation={[0, 0, 0.15]}>
        <boxGeometry args={[0.4, 0.1, 0.1]} />
        <meshStandardMaterial 
          color="#2a1810" 
          roughness={0.9}
        />
      </mesh>

      {/* Nose */}
      <mesh position={[0, 0.1, 1.4]} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[0.22, 0.5, 8]} />
        <meshStandardMaterial 
          color={`hsl(${hue}, 35%, 68%)`} 
          roughness={0.6}
        />
      </mesh>

      {/* Nose bridge */}
      <mesh position={[0, 0.35, 1.3]}>
        <boxGeometry args={[0.15, 0.5, 0.2]} />
        <meshStandardMaterial 
          color={`hsl(${hue}, 35%, 68%)`} 
          roughness={0.6}
        />
      </mesh>

      {/* Cheeks - subtle definition */}
      <Sphere args={[0.35, 32, 32]} position={[-0.65, -0.1, 1.1]}>
        <meshStandardMaterial 
          color={`hsl(${hue + 5}, 45%, 72%)`} 
          roughness={0.7}
        />
      </Sphere>
      <Sphere args={[0.35, 32, 32]} position={[0.65, -0.1, 1.1]}>
        <meshStandardMaterial 
          color={`hsl(${hue + 5}, 45%, 72%)`} 
          roughness={0.7}
        />
      </Sphere>

      {/* Mouth - realistic viseme animation */}
      {(isSpeaking || audioLevel > 0.05) && (
        <>
          {/* Open mouth cavity */}
          <Sphere 
            ref={mouthRef}
            args={[0.28, 32, 32]} 
            position={[0, mouthShape.posY, 1.15]} 
            scale={[mouthShape.scaleX, mouthShape.scaleY, mouthShape.scaleZ]}
          >
            <meshStandardMaterial 
              color="#6d3644"
              roughness={0.8}
            />
          </Sphere>
          
          {/* Upper lip */}
          <mesh position={[0, mouthShape.posY + 0.15, 1.25]}>
            <boxGeometry args={[0.45, 0.08, 0.12]} />
            <meshStandardMaterial 
              color={`hsl(${hue}, 50%, 65%)`}
              roughness={0.5}
            />
          </mesh>
          
          {/* Lower lip */}
          <mesh position={[0, mouthShape.posY - 0.15, 1.25]}>
            <boxGeometry args={[0.45, 0.1, 0.12]} />
            <meshStandardMaterial 
              color={`hsl(${hue}, 48%, 63%)`}
              roughness={0.5}
            />
          </mesh>

          {/* Teeth (when mouth open) */}
          {mouthShape.scaleY > 0.15 && (
            <>
              <mesh position={[0, mouthShape.posY + 0.05, 1.28]}>
                <boxGeometry args={[0.35, 0.08, 0.08]} />
                <meshStandardMaterial 
                  color="#f5f5dc"
                  roughness={0.3}
                />
              </mesh>
              <mesh position={[0, mouthShape.posY - 0.08, 1.28]}>
                <boxGeometry args={[0.35, 0.08, 0.08]} />
                <meshStandardMaterial 
                  color="#f5f5dc"
                  roughness={0.3}
                />
              </mesh>
            </>
          )}
        </>
      )}

      {/* Closed mouth when not speaking */}
      {!isSpeaking && audioLevel < 0.05 && (
        <>
          {/* Lips */}
          <mesh position={[0, -0.48, 1.23]}>
            <boxGeometry args={[0.5, 0.08, 0.15]} />
            <meshStandardMaterial 
              color={`hsl(${hue}, 50%, 60%)`}
              roughness={0.4}
            />
          </mesh>
          <mesh position={[0, -0.56, 1.23]}>
            <boxGeometry args={[0.5, 0.08, 0.15]} />
            <meshStandardMaterial 
              color={`hsl(${hue}, 48%, 58%)`}
              roughness={0.4}
            />
          </mesh>
          {/* Mouth line */}
          <mesh position={[0, -0.52, 1.29]}>
            <boxGeometry args={[0.42, 0.02, 0.02]} />
            <meshStandardMaterial 
              color="#5a2838"
              roughness={0.6}
            />
          </mesh>
        </>
      )}

      {/* Ears */}
      <mesh position={[-1.35, 0.1, 0]} rotation={[0, 0, -0.3]}>
        <boxGeometry args={[0.3, 0.6, 0.15]} />
        <meshStandardMaterial 
          color={`hsl(${hue}, 38%, 68%)`}
          roughness={0.7}
        />
      </mesh>
      <mesh position={[1.35, 0.1, 0]} rotation={[0, 0, 0.3]}>
        <boxGeometry args={[0.3, 0.6, 0.15]} />
        <meshStandardMaterial 
          color={`hsl(${hue}, 38%, 68%)`}
          roughness={0.7}
        />
      </mesh>

      {/* Chin definition */}
      <mesh position={[0, -0.85, 1.15]}>
        <boxGeometry args={[0.6, 0.3, 0.3]} />
        <meshStandardMaterial 
          color={`hsl(${hue}, 38%, 68%)`}
          roughness={0.65}
        />
      </mesh>
    </group>
  );
}

// Advanced audio analyzer for viseme-based lip sync
function useAudioAnalyzer(audioElement) {
  const [audioLevel, setAudioLevel] = useState(0);
  const [visemeWeights, setVisemeWeights] = useState({
    aa: 0, e: 0, i: 0, o: 0, u: 0, m: 0, l: 0, f: 0, s: 0
  });
  
  const analyzerRef = useRef(null);
  const audioContextRef = useRef(null);
  const smoothingRef = useRef({
    aa: 0, e: 0, i: 0, o: 0, u: 0, m: 0, l: 0, f: 0, s: 0
  });

  useEffect(() => {
    if (!audioElement) return;

    try {
      // Create audio context and analyzer with higher resolution
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyzer = audioContext.createAnalyser();
      analyzer.fftSize = 2048; // Higher resolution for better frequency detection
      analyzer.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaElementSource(audioElement);
      source.connect(analyzer);
      analyzer.connect(audioContext.destination);

      audioContextRef.current = audioContext;
      analyzerRef.current = analyzer;

      // Analyze audio for viseme detection
      const frequencyData = new Uint8Array(analyzer.frequencyBinCount);
      const sampleRate = audioContext.sampleRate;
      
      const detectVisemes = () => {
        if (!analyzerRef.current) return;
        
        analyzerRef.current.getByteFrequencyData(frequencyData);
        
        // Calculate overall audio level
        const average = frequencyData.reduce((a, b) => a + b) / frequencyData.length;
        const normalized = average / 255;
        setAudioLevel(normalized);

        if (normalized > 0.05) { // Only analyze if there's audio
          // Analyze frequency bands for viseme detection
          const getFrequencyBandEnergy = (lowFreq, highFreq) => {
            const lowIndex = Math.floor(lowFreq * analyzer.fftSize / sampleRate);
            const highIndex = Math.floor(highFreq * analyzer.fftSize / sampleRate);
            let sum = 0;
            for (let i = lowIndex; i < highIndex && i < frequencyData.length; i++) {
              sum += frequencyData[i];
            }
            return (sum / (highIndex - lowIndex)) / 255;
          };

          // Detect phoneme categories based on frequency analysis
          const lowEnergy = getFrequencyBandEnergy(50, 300);      // Bass - closed sounds (M, B, P)
          const midLowEnergy = getFrequencyBandEnergy(300, 700);  // Low-mid - open vowels (AA, O)
          const midEnergy = getFrequencyBandEnergy(700, 1500);    // Mid - medium vowels (E, U)
          const midHighEnergy = getFrequencyBandEnergy(1500, 3000); // Mid-high - high vowels (I)
          const highEnergy = getFrequencyBandEnergy(3000, 8000);  // High - fricatives (S, F)

          // Map energy levels to viseme weights with smoothing
          const SMOOTH_FACTOR = 0.3; // Lower = smoother transitions
          
          const newWeights = {
            m: lowEnergy * 1.2,                    // Closed mouth sounds
            aa: midLowEnergy * 1.5,               // Open vowels
            o: midLowEnergy * midEnergy * 0.8,    // Rounded sounds
            e: midEnergy * 1.2,                   // Medium vowels
            u: midEnergy * lowEnergy * 0.7,       // Puckered sounds
            i: midHighEnergy * 1.3,               // High vowels
            s: highEnergy * 1.5,                  // Sibilants
            f: highEnergy * midHighEnergy * 0.6,  // Fricatives
            l: midEnergy * midHighEnergy * 0.5    // Liquids
          };

          // Apply smoothing to prevent jitter
          Object.keys(newWeights).forEach(key => {
            smoothingRef.current[key] = 
              smoothingRef.current[key] * (1 - SMOOTH_FACTOR) + 
              newWeights[key] * SMOOTH_FACTOR;
          });

          // Normalize weights so they sum to approximately 1
          const total = Object.values(smoothingRef.current).reduce((a, b) => a + b, 0);
          if (total > 0) {
            Object.keys(smoothingRef.current).forEach(key => {
              smoothingRef.current[key] = Math.min(1, smoothingRef.current[key] / total);
            });
          }

          setVisemeWeights({ ...smoothingRef.current });
        } else {
          // Fade out visemes when silent
          Object.keys(smoothingRef.current).forEach(key => {
            smoothingRef.current[key] *= 0.85;
          });
          setVisemeWeights({ ...smoothingRef.current });
        }
        
        requestAnimationFrame(detectVisemes);
      };

      detectVisemes();

    } catch (err) {
      console.error('Audio analyzer error:', err);
    }

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [audioElement]);

  return { audioLevel, visemeWeights };
}

// GLB Model Loader Component (for future use with real models)
function GLBAvatar({ modelPath, isSpeaking, visemeWeights }) {
  const gltf = useGLTF(modelPath);
  const modelRef = useRef();
  
  useFrame((state) => {
    if (modelRef.current) {
      // Idle animation
      if (!isSpeaking) {
        modelRef.current.position.y = Math.sin(state.clock.elapsedTime) * 0.05;
        modelRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.03;
      }
      
      // Apply viseme morphs if model has them
      if (gltf.scene.children[0]?.morphTargetInfluences && visemeWeights) {
        // Map viseme weights to morph targets
        // This will be configured once we have real models with morph targets
      }
    }
  });
  
  return <primitive ref={modelRef} object={gltf.scene} scale={1.5} />;
}

// Main 3D Avatar Component with viseme-based lip sync
export default function Avatar3D({ 
  isSpeaking, 
  audioElement, 
  className = '',
  gender = 'neutral', // 'male', 'female', or 'neutral'
  useGLBModel = false, // Set to true when GLB models are ready
  customModelPath = null
}) {
  const { audioLevel, visemeWeights } = useAudioAnalyzer(audioElement);

  // Determine which model to use
  const getModelPath = () => {
    if (customModelPath) return customModelPath;
    if (gender === 'male') return '/models/male_head.glb';
    if (gender === 'female') return '/models/female_head.glb';
    return null; // Use geometric avatar as fallback
  };

  const modelPath = getModelPath();

  return (
    <div className={`w-full h-full ${className}`}>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        style={{ background: 'transparent' }}
        shadows
      >
        {/* Natural studio lighting setup */}
        <ambientLight intensity={0.5} />
        
        {/* Key light - main illumination */}
        <directionalLight
          position={[5, 5, 5]}
          intensity={1.0}
          color="#fff5e6"
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        
        {/* Fill light - softens shadows */}
        <pointLight position={[-4, 3, -1]} intensity={0.5} color="#ffe6d5" />
        
        {/* Rim light - edge definition */}
        <pointLight position={[0, -1, -4]} intensity={0.3} color="#a8c5dd" />
        
        {/* Back light - depth and separation */}
        <spotLight
          position={[0, 6, -2]}
          angle={0.5}
          penumbra={1}
          intensity={0.6}
          color="#ffd4a3"
        />
        
        {/* Side accent - dimension */}
        <pointLight position={[4, 1, 2]} intensity={0.4} color="#fff9e6" />

        {/* Render GLB model or geometric avatar */}
        {useGLBModel && modelPath ? (
          <Suspense fallback={<AvatarHead isSpeaking={isSpeaking} audioLevel={audioLevel} visemeWeights={visemeWeights} />}>
            <GLBAvatar 
              modelPath={modelPath}
              isSpeaking={isSpeaking}
              visemeWeights={visemeWeights}
            />
          </Suspense>
        ) : (
          <AvatarHead 
            isSpeaking={isSpeaking} 
            audioLevel={audioLevel}
            visemeWeights={visemeWeights}
          />
        )}

        {/* Interactive controls */}
        <OrbitControls
          enableZoom={true}
          minDistance={3}
          maxDistance={8}
          enablePan={false}
          minPolarAngle={Math.PI / 4}
          maxPolarAngle={Math.PI / 1.8}
          target={[0, 0.2, 0]}
        />
      </Canvas>
    </div>
  );
}

// Simpler version for preview (no audio analysis)
export function Avatar3DSimple({ isSpeaking = false, className = '' }) {
  const defaultVisemes = isSpeaking 
    ? { aa: 0.6, e: 0.3, i: 0.2, o: 0.1, u: 0.1, m: 0.05, l: 0.05, f: 0.05, s: 0.1 }
    : { aa: 0, e: 0, i: 0, o: 0, u: 0, m: 0, l: 0, f: 0, s: 0 };

  return (
    <div className={`w-full h-full ${className}`}>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} />
        <pointLight position={[-5, 3, -2]} intensity={0.6} color="#fff5e6" />
        
        <AvatarHead 
          isSpeaking={isSpeaking} 
          audioLevel={isSpeaking ? 0.5 : 0}
          visemeWeights={defaultVisemes}
        />
        
        <OrbitControls
          enableZoom={false}
          enablePan={false}
          minPolarAngle={Math.PI / 3}
          maxPolarAngle={Math.PI / 1.5}
        />
      </Canvas>
    </div>
  );
}
